import { DialogCloseEvent } from './dialog.js';
import { clamp, preventDefault } from './utilities.js';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
/* Open / reset at 1× (fit viewport). Second click/tap → detail, at least 2×. */
const DEFAULT_ZOOM = 1;
const DETAIL_ZOOM = 2;
const DRAG_THRESHOLD = 10;
const ZOOM_TOLERANCE = 0.05;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom and drag.
 */

/** @extends {Component<Refs>} */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller = new AbortController();
  /** @type {number} */
  #scale = DEFAULT_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = DEFAULT_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  /** @type {boolean} */
  #ignoreClick = false;

  get #image() {
    return this.refs.image;
  }

  get #isZoomed() {
    return this.#scale > MIN_ZOOM + ZOOM_TOLERANCE;
  }

  connectedCallback() {
    super.connectedCallback();
    // Closed lightbox must not pay ResizeObserver + touch listeners on PDP idle.
    if (this.closest('dialog')?.open) this.activate();
  }

  /**
   * Bind pan/pinch listeners. Call when the lightbox opens (or if already open).
   */
  activate() {
    if (this.#initialized) return;
    if (!this.#image) return;

    this.#initResizeListener();
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('click', this.#handleClick, { signal });
    // Prestige-style: while in detail zoom, image pans with the mouse.
    this.addEventListener('pointermove', this.#handlePointerMove, { signal });
    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (!this.#initialized) {
      this.#initEventListeners();
    }

    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * Toggle fit ↔ detail. Optional point defaults to the wrapper center (zoom button).
   * @param {number} [clientX]
   * @param {number} [clientY]
   */
  toggleZoom(clientX, clientY) {
    this.activate();
    const rect = this.getBoundingClientRect();
    const x = clientX ?? rect.left + rect.width / 2;
    const y = clientY ?? rect.top + rect.height / 2;
    this.#toggleZoomAtPoint(x, y);
  }

  resetZoom() {
    this.#resetZoom();
  }

  /**
   * @returns {'fit' | 'detail'}
   */
  getState() {
    return this.#isZoomed ? 'detail' : 'fit';
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (event.target instanceof Element) {
      if (
        event.target.closest(
          '[data-zoom-toggle], [data-zoom-close], [data-zoom-prev], [data-zoom-next], [data-zoom-thumbs]'
        )
      ) {
        return;
      }
    }

    if (this.#ignoreClick || this.#hasDraggedBeyondThreshold) {
      this.#ignoreClick = false;
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    // Fire and forget: waiting for the full-res image would stall the toggle on slow connections.
    if (!this.#isZoomed) {
      const dialog = this.closest('zoom-dialog');
      const media = this.closest('li');
      if (dialog && typeof dialog.loadHighResolutionImage === 'function' && media instanceof HTMLElement) {
        dialog.loadHighResolutionImage(media);
      }
    }

    this.#toggleZoomAtPoint(event.clientX, event.clientY);
  };

  /**
   * Desktop: pan follows the pointer while zoomed (Prestige-style).
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    if (event.pointerType === 'touch') return;
    if (!this.#isZoomed) return;

    this.#panToPointer(event.clientX, event.clientY);
  };

  /**
   * Map pointer position inside the wrapper to image translation (0…1 → edges).
   * Cursor on the left shows the left of the image, etc.
   * @param {number} clientX
   * @param {number} clientY
   */
  #panToPointer(clientX, clientY) {
    if (!this.#isZoomed) return;

    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const { maxTranslateX, maxTranslateY } = this.#getMaxTranslate();
    const ratioX = clamp((clientX - rect.left) / rect.width, 0, 1);
    const ratioY = clamp((clientY - rect.top) / rect.height, 0, 1);

    this.#translate.x = (0.5 - ratioX) * 2 * maxTranslateX;
    this.#translate.y = (0.5 - ratioY) * 2 * maxTranslateY;
    this.#requestUpdateTransform();
  }

  /**
   * Painted size of the image inside `box` under `object-fit: contain`.
   * @param {{ width: number, height: number }} box
   * @returns {{ width: number, height: number } | null}
   */
  #getContainedImageSize(box) {
    if (!box.width || !box.height || !this.#image) return null;

    const imageElement = this.#image;
    const hasNaturalSize = imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0;
    // Assume square until the image reports its natural size, so bounds stay conservative.
    const imageAspectRatio = hasNaturalSize ? imageElement.naturalWidth / imageElement.naturalHeight : 1;

    if (imageAspectRatio > box.width / box.height) {
      return { width: box.width, height: box.width / imageAspectRatio };
    }

    return { width: box.height * imageAspectRatio, height: box.height };
  }

  /**
   * Zoom step that fills the screen edge to edge, so detail never shows letterbox bars.
   * `contain` always leaves one axis short — a portrait shot fits the height and stops at
   * ~90% width on 16:9 — so grow the step until the short axis reaches the edge. Never
   * below DETAIL_ZOOM, so slides that already fill keep today's zoom.
   * @returns {number}
   */
  #getDetailZoom() {
    // At fit the wrapper is still kadr-sized; the slide container is the detail-state box.
    const target = this.closest('.product-media-container') ?? this;
    const targetRect = target.getBoundingClientRect();
    const contained = this.#getContainedImageSize(targetRect);

    if (!contained?.width || !contained.height) return DETAIL_ZOOM;

    const coverScale = Math.max(targetRect.width / contained.width, targetRect.height / contained.height);

    return clamp(Math.max(DETAIL_ZOOM, coverScale), MIN_ZOOM, MAX_ZOOM);
  }

  /**
   * How far the image may travel before its edge would enter the wrapper.
   * @returns {{ maxTranslateX: number, maxTranslateY: number }}
   */
  #getMaxTranslate() {
    const wrapperRect = this.getBoundingClientRect();
    const contained = this.#getContainedImageSize(wrapperRect);

    if (!contained) return { maxTranslateX: 0, maxTranslateY: 0 };

    const horizontalOverflow = Math.max(0, contained.width * this.#scale - wrapperRect.width);
    const verticalOverflow = Math.max(0, contained.height * this.#scale - wrapperRect.height);

    return {
      maxTranslateX: horizontalOverflow / 2 / this.#scale,
      maxTranslateY: verticalOverflow / 2 / this.#scale,
    };
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      preventDefault(event);
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      this.#setTransformWillChange(true);
      this.#startZoomGestureFromTouches(touch1, touch2);
      return;
    }

    if (touchCount !== 1) return;

    const touch = event.touches[0];
    if (!touch) return;

    // Do not preventDefault on touchstart — that would block the click toggle.
    // Fit (1×): parent lightbox can scroll horizontally.
    // Detail: pan starts on touchmove once past the drag threshold.
    this.#startDragGestureFromTouch(touch);
  };

  /**
   * Start a zoom gesture with two touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * Start a drag gesture with a single touch
   * @param {Touch} touch
   */
  #startDragGestureFromTouch(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  /**
   * Toggle between fit (1×) and detail, centered on the interaction point.
   * @param {number} clientX
   * @param {number} clientY
   */
  #toggleZoomAtPoint(clientX, clientY) {
    let targetZoom;

    if (this.#hasManualZoom) {
      targetZoom = MIN_ZOOM;
      this.#hasManualZoom = false;
      this.#translate = { x: 0, y: 0 };
    } else if (Math.abs(this.#scale - MIN_ZOOM) < ZOOM_TOLERANCE) {
      targetZoom = this.#getDetailZoom();
    } else {
      targetZoom = MIN_ZOOM;
    }

    if (targetZoom !== MIN_ZOOM) {
      this.#scale = Math.min(MAX_ZOOM, targetZoom);
      this.#syncZoomStateAttr();
      // Align pan with cursor immediately (Prestige mouse-follow start point).
      this.#panToPointer(clientX, clientY);
    } else {
      this.#scale = targetZoom;
      this.#translate = { x: 0, y: 0 };
      this.#syncZoomStateAttr();
      this.#requestUpdateTransform();
    }
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      preventDefault(event);
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
      return;
    }

    if (touchCount !== 1 || !this.#isDragging) return;

    const touch = event.touches[0];
    if (!touch) return;

    // At fit zoom, release the gesture so the lightbox can scroll horizontally.
    // Still mark movement so a post-swipe synthetic click does not toggle 2×.
    if (!this.#isZoomed) {
      if (getDistance(touch, this.#startPosition) >= DRAG_THRESHOLD) {
        this.#hasDraggedBeyondThreshold = true;
        this.#ignoreClick = true;
      }
      this.#isDragging = false;
      return;
    }

    preventDefault(event);
    this.#setTransformWillChange(true);
    this.#processDragGesture(touch);
  };

  /**
   * Process zoom gesture from touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;
    const currentDistance = getDistance(touch1, touch2);
    const oldScale = this.#scale;
    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);
    this.#hasManualZoom = true;

    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;
    const distanceFromCenterX = midX - containerCenterX;
    const distanceFromCenterY = midY - containerCenterY;
    const scaleDelta = this.#scale / oldScale - 1.0;

    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;

    this.#syncZoomStateAttr();
    this.#setTransformWillChange(true);
    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * Process drag gesture from touch
   * @param {Touch} touch
   */
  #processDragGesture(touch) {
    const distance = getDistance(touch, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    const dx = touch.clientX - this.#startPosition.x;
    const dy = touch.clientY - this.#startPosition.y;

    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      if (this.#hasDraggedBeyondThreshold) {
        this.#ignoreClick = true;
      }

      this.#isDragging = false;
      this.#requestUpdateTransform();
      this.#hasDraggedBeyondThreshold = false;
      this.#setTransformWillChange(false);
    }
  };

  /**
   * @param {boolean} active
   */
  #setTransformWillChange(active) {
    if (!this.#image) return;
    this.#image.style.willChange = active ? 'transform' : 'auto';
  }

  #syncZoomStateAttr() {
    const next = this.#isZoomed ? 'detail' : 'fit';
    if (this.dataset.zoomState === next) return;

    this.dataset.zoomState = next;
    this.dispatchEvent(
      new CustomEvent('zoom-state-change', {
        bubbles: true,
        detail: { state: next },
      })
    );
  }

  /**
   * Constrain image translation to keep it within the viewport
   */
  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight || !this.#image) return;

    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    const { maxTranslateX, maxTranslateY } = this.#getMaxTranslate();
    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);

    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
    this.#syncZoomStateAttr();
  };

  /**
   * Reset zoom to default state (1× scale, centered position)
   * Called when zoom is exited/closed
   */
  #resetZoom = () => {
    this.#scale = DEFAULT_ZOOM;
    this.#startScale = DEFAULT_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#hasDraggedBeyondThreshold = false;
    this.#hasManualZoom = false;
    this.#ignoreClick = false;
    this.#setTransformWillChange(false);

    this.style.setProperty('--drag-zoom-scale', DEFAULT_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
    this.#syncZoomStateAttr();
  };
}

/**
 * Calculate distance between two points or touches
 * @param {Point | Touch | PointerEvent} point1 - First point or touch
 * @param {Point | Touch | PointerEvent} point2 - Second point or touch
 * @returns {number} Distance between the points
 */
function getDistance(point1, point2) {
  const x1 =
    /** @type {Point} */ (point1).x ??
    /** @type {Touch} */ (point1).clientX ??
    /** @type {PointerEvent} */ (point1).clientX;
  const y1 =
    /** @type {Point} */ (point1).y ??
    /** @type {Touch} */ (point1).clientY ??
    /** @type {PointerEvent} */ (point1).clientY;
  const x2 =
    /** @type {Point} */ (point2).x ??
    /** @type {Touch} */ (point2).clientX ??
    /** @type {PointerEvent} */ (point2).clientX;
  const y2 =
    /** @type {Point} */ (point2).y ??
    /** @type {Touch} */ (point2).clientY ??
    /** @type {PointerEvent} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */
